import { Controller } from "@hotwired/stimulus";
import { DocstoreDirectUpload } from "../packs/docstore_direct_upload";
import { useSubmitButtonManager } from "../mixins/useSubmitButtonManager";
import { useTemplateManager } from "../mixins/useTemplateManager";

export default class extends Controller {
  static targets = ["fileInput", "uploadInfo", "selectedFileInfo", "uploadWrapper", "fileItemTemplate"];
  static values = { 
    uploadUrl: String,
    directUploadField: String,
    maxFileSize: Number,
    evidenceType: String,
    additionalHiddenInputs: Object
  };

  connect() {
    // Add submit button management capabilities
    useSubmitButtonManager(this);
    // Add template management capabilities
    useTemplateManager(this);
    
    this.prepareForm();
    this.fileInputTarget.addEventListener('change', this.handleFileChange.bind(this));
  }

  disconnect() {
    this.fileInputTarget.removeEventListener('change', this.handleFileChange.bind(this));
  }

  prepareForm() {
    // Generate the progress meter if it doesn't exist
    this.maybeCreateUploadElements();

    // Improve the styling of the 'Select Files...' button
    this.maybeStyleFileInput();
  }

  maybeCreateUploadElements() {
    if (this.hasUploadInfoTarget) return;

    // Create upload info container
    const uploadInfo = Object.assign(document.createElement('div'), { 
      className: 'docstore-upload-info form-control', 
      style: 'display: none' 
    });
    uploadInfo.setAttribute('data-direct-upload-target', 'uploadInfo');

    // Create selected file info list
    const selectedFileInfo = Object.assign(document.createElement('ul'), { 
      className: 'selected-file-info' 
    });
    selectedFileInfo.setAttribute('data-direct-upload-target', 'selectedFileInfo');
    selectedFileInfo.style.display = 'none';

    // Insert elements
    this.fileInputTarget.parentNode.insertBefore(uploadInfo, this.fileInputTarget);
    uploadInfo.appendChild(selectedFileInfo);
  }

  maybeStyleFileInput() {
    if (this.hasUploadWrapperTarget) return;

    const wrapper = Object.assign(document.createElement('div'), { 
      className: 'file-upload-wrapper btn' 
    });
    wrapper.setAttribute('data-direct-upload-target', 'uploadWrapper');
    
    this.fileInputTarget.parentNode.insertBefore(wrapper, this.fileInputTarget);
    wrapper.appendChild(this.fileInputTarget);
    wrapper.insertBefore(
      Object.assign(document.createElement('span'), { 
        className: 'btn btn-secondary', 
        textContent: 'Add Files' 
      }), 
      wrapper.firstChild
    );
  }

  handleFileChange(event) {
    const files = event.target.files;
    if (files.length > 0) {
      this.selectedFileInfoTarget.style.display = '';
      this.validateFileSize(files);
      this.addFiles(files);
    }
  }

  createFileItem(file, fileUniqueId) {
    const fileItem = this.instantiateTemplate({
      template: this.fileItemTemplateTarget,
      content: {
        '[data-filename-placeholder]': { textContent: file.name }
      },
      rootElement: {
        dataset: { uniqueId: fileUniqueId }
      }
    });

    const removeButton = fileItem.querySelector('.file-remove-btn');
    if (removeButton) {
      removeButton.setAttribute('data-file-id', fileUniqueId);
    }

    return fileItem;
  }

  validateFileSize(files) {
    const maxFileSize = this.maxFileSizeValue;

    for (let i = 0; i < files.length; i++) {
      if (files[i].size > maxFileSize) {
        alert('File size exceeds the maximum limit.');
        return false;
      }
    }
    return true;
  }

  addFiles(files) {
    const directUploadUrl = this.uploadUrlValue;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileUniqueId = Math.random().toString(36).substring(2, 16);

      this.disableSubmitButton(`upload-${fileUniqueId}`);

      const fileItem = this.createFileItem(file, fileUniqueId);
      this.selectedFileInfoTarget.appendChild(fileItem);

      // Show the progress bar for this file upload
      const progressElement = fileItem.querySelector('.file-progress');
      if (progressElement) {
        progressElement.style.display = 'block';
      }

      // Store the original name attribute before removing it
      if (!this.fileInputTarget.getAttribute('data-original-name')) {
        this.fileInputTarget.setAttribute('data-original-name', this.fileInputTarget.getAttribute('name') || 'file');
      }
      
      // Remove the name attribute so the field is no longer submitted
      this.fileInputTarget.removeAttribute('name');

      const upload = new DocstoreDirectUpload(file, directUploadUrl);

      this.uploadInfoTarget.style.display = 'block';

      upload.create((error, formData) => {
        if (error) {
          alert(error);
          this.enableSubmitButton(`upload-${fileUniqueId}`);
          // Hide the progress bar on error
          if (progressElement) {
            progressElement.style.display = 'none';
          }
        } else {
          this.uploadCompleted(formData, fileUniqueId);
        }
      });
    }
  }

  setUploadProgress(fileListItem) {
    const progressElement = fileListItem.querySelector('.file-progress');
    if (progressElement) {
      const bar = progressElement.querySelector('.progress-bar');
      if (bar) {
        bar.style.width = '100%';
        bar.setAttribute('aria-valuenow', '100');
      }
      
      // Hide the progress bar after a short delay to show completion
      setTimeout(() => {
        progressElement.style.display = 'none';
      }, 500);
    }
  }

  uploadCompleted(formData, fileUniqueId) {
    const targetItem = this.selectedFileInfoTarget.querySelector(`[data-unique-id="${fileUniqueId}"]`);
    if (!targetItem) {
      console.error('Could not find target item with unique ID:', fileUniqueId);
      return;
    }
    const hiddenInputsContainer = targetItem.querySelector('[data-hidden-inputs-container]');
    if (!hiddenInputsContainer) {
      console.error('Could not find hidden inputs container in target item:', targetItem);
      return;
    }
    const directUploadField = this.directUploadFieldValue;

    // Set progress bar to 100%, then hide it
    this.setUploadProgress(targetItem)

    if (formData.binaries && formData.binaries[0]) {
      targetItem.setAttribute('data-docstore-id', formData.binaries[0].id);
    }

    // Insert hidden inputs to inject the values for form submit
    const nameInput = Object.assign(document.createElement('input'), { 
      type: 'hidden', 
      name: `${directUploadField}[name]`, 
      value: formData.file_name 
    });
    hiddenInputsContainer.appendChild(nameInput);

    const docStoreUrl = Object.assign(document.createElement('input'), { 
      type: 'hidden', 
      name: `${directUploadField}[upload_url]`, 
      value: 'docstore://' + formData.binaries[0].id 
    });
    hiddenInputsContainer.appendChild(docStoreUrl);

    Object.entries(this.additionalHiddenInputsValue).forEach(([key, value]) => {
      let additionalInput = Object.assign(document.createElement('input'), { 
        type: 'hidden', 
        name: `${directUploadField}[${key}]`, 
        value: value
      });
      hiddenInputsContainer.appendChild(additionalInput);
    });

    this.uploadInfoTarget.style.display = '';

    this.enableSubmitButton(`upload-${fileUniqueId}`);

    // Dispatch event for document upload completion
    if (formData.binaries && formData.binaries[0]) {
      this.dispatch('documentUploaded', {
        detail: { docstoreId: formData.binaries[0].id, filename: formData.binaries[0].metadata?.name || 'unknown-file' }
      });
    }
  }

  removeFile(event) {
    // Click event might register on the button's icon/contents, so find the button element itself
    const button = event.target.closest('.file-remove-btn');
    const fileId = button ? button.getAttribute('data-file-id') : null;
    
    if (!fileId) {
      console.error("Could not find file ID for removal on target with classes:", event.target.classList);
      return;
    }
    
    const targetItem = this.selectedFileInfoTarget.querySelector(`[data-unique-id="${fileId}"]`);
    
    if (targetItem) {
      // Get the docstore ID before removing the element
      const docstoreId = targetItem.getAttribute('data-docstore-id');
      
      // Remove the file item from the DOM
      targetItem.remove();
      
      // Remove the lock on the submit button
      this.enableSubmitButton(`upload-${fileId}`);
      
      // Dispatch event for document removal if docstore ID exists
      if (docstoreId) {
        this.dispatch('documentRemoved', {
          detail: { docstoreId: docstoreId }
        });
      }
      
      // Clear the file input value so the same file can be selected again
      this.fileInputTarget.value = '';
      
      // If no more files, hide the selected file info and upload info
      if (this.selectedFileInfoTarget.children.length === 0) {
        this.selectedFileInfoTarget.style.display = 'none';
        this.uploadInfoTarget.style.display = 'none';
        
        // Restore the name attribute to the file input so it can be used again
        this.fileInputTarget.setAttribute('name', this.fileInputTarget.getAttribute('data-original-name') || 'file');
      }
    }
  }
}
